import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AuthUser,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
} from "@aws-amplify/auth";
import {
  Authenticator,
  Button,
  Heading,
  Image,
  Text,
  useAuthenticator,
  useTheme,
  View,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

import logoImg from "../../assets/logo.svg";
import emailIcon from "../../assets/email.svg";
import lockIcon from "../../assets/lock.svg";
import { Shield } from "lucide-react";

// -----------------------------------------------------------------------------
// Constants (use imported assets so they resolve in build)
// -----------------------------------------------------------------------------
const LOGO_SRC = logoImg;

// -----------------------------------------------------------------------------
// Client-side rate limiting: after 5 failed attempts, lock form for 30s; double each batch
// -----------------------------------------------------------------------------
const FAILED_ATTEMPTS_BEFORE_LOCK = 5;
const INITIAL_COOLDOWN_MS = 30_000;

type LoginRateLimitContextValue = {
  failedAttempts: number;
  cooldownRemaining: number;
  isLocked: boolean;
  recordFailure: () => void;
  recordSuccess: () => void;
};

const LoginRateLimitContext = React.createContext<LoginRateLimitContextValue | null>(null);

function LoginRateLimitProvider({ children }: { children: React.ReactNode }) {
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [cooldownEndTime, setCooldownEndTime] = useState<number | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!cooldownEndTime) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      if (Date.now() >= cooldownEndTime) setCooldownEndTime(null);
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownEndTime]);

  const recordFailure = useCallback(() => {
    setFailedAttempts((prev) => {
      const next = prev + 1;
      if (next % FAILED_ATTEMPTS_BEFORE_LOCK === 0) {
        const batch = Math.floor(next / FAILED_ATTEMPTS_BEFORE_LOCK);
        const cooldownMs = INITIAL_COOLDOWN_MS * Math.pow(2, batch - 1);
        setCooldownEndTime(Date.now() + cooldownMs);
      }
      return next;
    });
  }, []);

  const recordSuccess = useCallback(() => {
    setFailedAttempts(0);
    setCooldownEndTime(null);
  }, []);

  const cooldownRemaining =
    cooldownEndTime ? Math.max(0, Math.ceil((cooldownEndTime - Date.now()) / 1000)) : 0;
  const isLocked = cooldownRemaining > 0;

  const value: LoginRateLimitContextValue = {
    failedAttempts,
    cooldownRemaining,
    isLocked,
    recordFailure,
    recordSuccess,
  };

  return (
    <LoginRateLimitContext.Provider value={value}>
      {children}
    </LoginRateLimitContext.Provider>
  );
}

// -----------------------------------------------------------------------------
// Password toggle icons (DOM elements, no innerHTML — avoids scanner flags)
// -----------------------------------------------------------------------------
const SVG_NS = "http://www.w3.org/2000/svg";

function createPasswordToggleSvg(paths: string[]): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "password-toggle-icon");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  paths.forEach((d) => {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    svg.appendChild(path);
  });
  return svg;
}

function createEyeOpenSvg(): SVGSVGElement {
  return createPasswordToggleSvg([
    "M15 12a3 3 0 11-6 0 3 3 0 016 0z",
    "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
  ]);
}

function createEyeClosedSvg(): SVGSVGElement {
  return createPasswordToggleSvg([
    "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878a4.5 4.5 0 106.262 6.262M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21",
  ]);
}

function setPasswordToggleIcon(btn: HTMLElement, visible: boolean): void {
  btn.setAttribute("data-icon-state", visible ? "closed" : "open");
  while (btn.firstChild) btn.removeChild(btn.firstChild);
  btn.appendChild(visible ? createEyeClosedSvg() : createEyeOpenSvg());
}

// -----------------------------------------------------------------------------
// Redirect after successful login
// -----------------------------------------------------------------------------
const LoginRedirect = ({ user }: { user: AuthUser | undefined }) => {
  const navigate = useNavigate();
  const rateLimit = useContext(LoginRateLimitContext);
  useEffect(() => {
    if (!user) return;
    rateLimit?.recordSuccess();
    navigate("/dashboard", { replace: true });
  }, [user, navigate, rateLimit]);

  return (
    <div className="fixed inset-0 bg-black/60 from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center z-50">
      <div className="flex flex-col items-center justify-center">
        <div className="relative">
          {/* Outer spinning ring */}
          <div className="w-24 h-24 border-4 border-[#e761a0] border-t-[#e761a0] rounded-full animate-spin"></div>
          {/* Inner icon */}
          <Shield className="w-8 h-8 text-[#e761a0] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-[#e761a0] text-lg mt-6 font-bold">Loading...</p>
        <p className="text-white text-sm mt-2">
          Please wait while we verify your session
        </p>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Shared Amplify route UI (reused for ConfirmSignUp, SetupTotp, etc.)
// -----------------------------------------------------------------------------
const DefaultRouteHeader = ({ title }: { title: string }) => {
  const { tokens } = useTheme();
  return (
    <Heading padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`} level={3}>
      {title}
    </Heading>
  );
};

const DefaultRouteFooter = () => <Text>Footer Information</Text>;

const ChangePasswordHeader = () => (
  <div className="login-wrapper">
    <div className="login-img mx-auto d-flex justify-content-center align-items-center">
      <Image
        alt="BuildCompliance"
        src={LOGO_SRC}
        className="login-img-logo mx-auto"
      />
    </div>
    <div className="head-wrap">
      <h2 className="custom-heading f-36 font-weight-300 text-center text-white">
        Create your password
      </h2>
      <p
        className="text-center text-white f-16 font-weight-300 mb-30"
        style={{ color: "rgba(255,255,255,0.8)" }}
      >
        Please create a secure password for this account
      </p>
    </div>
  </div>
);


// -----------------------------------------------------------------------------
// Main Login page
// -----------------------------------------------------------------------------
const Login = () => {
  const router = useNavigate();
  const [totpUsername, setTotpUsername] = useState("user@example.com");

  // During Setup TOTP, Cognito/Amplify may not yet provide an `email` claim,
  // but the user *did* enter their email on the Sign In screen. Cache it so
  // the QR label can still show the real email.
  const SIGNIN_EMAIL_CACHE_KEY = "bc:lastSignInEmail";

  useEffect(() => {
    const root = document.querySelector(
      ".authenticator-wrapper [data-amplify-router-content]",
    );
    if (!root) return;

    const captureEmail = () => {
      const input = root.querySelector<HTMLInputElement>(
        "form[data-amplify-authenticator-signin] input[name='username']",
      );
      const value = (input?.value || "").trim();
      if (!value) return;
      try {
        window.sessionStorage.setItem(SIGNIN_EMAIL_CACHE_KEY, value);
      } catch {
        // ignore storage errors
      }
    };

    // Capture immediately and on subsequent typing/renders.
    captureEmail();
    const onInput = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (
        target instanceof HTMLInputElement &&
        target.name === "username" &&
        target.closest("form[data-amplify-authenticator-signin]")
      ) {
        captureEmail();
      }
    };
    document.addEventListener("input", onInput, true);

    const observer =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => captureEmail())
        : null;
    observer?.observe(root, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("input", onInput, true);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadEmail = async () => {
      try {
        // Prefer email from ID token payload if available
        const session = await fetchAuthSession();
        const emailFromToken =
          (session as any)?.tokens?.idToken?.payload?.email ??
          (session as any)?.tokens?.accessToken?.payload?.email;

        if (typeof emailFromToken === "string" && emailFromToken.trim()) {
          if (!cancelled) setTotpUsername(emailFromToken.trim());
          return;
        }

        // Fallback: use cached sign-in email (works during Setup TOTP route)
        try {
          const cached = window.sessionStorage.getItem(SIGNIN_EMAIL_CACHE_KEY);
          if (typeof cached === "string" && cached.trim()) {
            if (!cancelled) setTotpUsername(cached.trim());
            return;
          }
        } catch {
          // ignore storage errors
        }

        // Fallback: current user username (often email in Cognito setups)
        const user = await getCurrentUser();
        const username = (user as any)?.username;
        if (typeof username === "string" && username.trim()) {
          if (!cancelled) setTotpUsername(username.trim());
        }
      } catch {
        // ignore; keep placeholder
      }
    };

    loadEmail();
    return () => {
      cancelled = true;
    };
  }, []);

  const formFields = useMemo(
    () => ({
      signIn: {
        username: {
          label: "",
          placeholder: "email@company.com",
          isRequired: true,
          className: "custom-input",
        },
        password: {
          label: "",
          placeholder: "••••••••••••••",
          isRequired: true,
          hideShowPassword: true,
        },
      },
      signUp: {
        username: {
          label: "",
          placeholder: "email@company.com",
          isRequired: true,
          order: 1,
        },
        password: {
          label: "",
          placeholder: "Create a strong password",
          isRequired: true,
          order: 2,
        },
        confirm_password: {
          label: "",
          placeholder: "Repeat your password",
          isRequired: true,
          order: 3,
        },
      },
      forceNewPassword: {
        password: {
          label: "",
          placeholder: "New password",
          isRequired: true,
          hideShowPassword: true,
        },
        confirm_password: {
          label: "",
          placeholder: "Confirm new password",
          isRequired: true,
          hideShowPassword: true,
        },
      },
      forgotPassword: {
        username: {
          label: "",
          placeholder: "email@company.com",
          isRequired: true,
        },
      },
      confirmSignUp: {
        confirmation_code: {
          label: "",
          placeholder: "Enter 6-digit code",
          isRequired: true,
        },
      },
      confirmResetPassword: {
        confirmation_code: {
          label: "Verification Code",
          placeholder: "Enter code from email",
          isRequired: true,
        },
        password: {
          label: "New password",
          placeholder: "Create new password",
          isRequired: true,
          hideShowPassword: true,
        },
        confirm_password: {
          label: "Confirm password",
          placeholder: "••••••••••••••",
          isRequired: true,
          hideShowPassword: true,
        },
      },
      // Amplify UI expects `setupTOTP` (not `setupTotp`) for TOTP QR customization.
      setupTOTP: {
        QR: {
          totpIssuer: "Build Compliance",
          totpUsername,
        },
        confirmation_code: {
          label: "Authenticator Code",
          placeholder: "Enter code from your app",
          isRequired: true,
        },
      },
      confirmSignIn: {
        confirmation_code: {
          label: "Verification Code",
          placeholder: "Enter your verification code",
          isRequired: true,
        },
      },
      setupEmail: {
        email: {
          label: "Email Address",
          placeholder: "Enter your email for MFA",
          isRequired: true,
        },
      },
    }),
    [totpUsername],
  );

  // -----------------------------------------------------------------------------
  // Custom services: intercept forgotPassword; ensure confirmSignIn gets challengeResponse for force new password
  // -----------------------------------------------------------------------------
  const services = {

    async handleConfirmSignIn(
      input: Parameters<typeof confirmSignIn>[0],
    ): Promise<Awaited<ReturnType<typeof confirmSignIn>>> {
      const rawInput = input as any;

      const challengeResponse =
        rawInput.challengeResponse ??
        rawInput.password ??
        rawInput.newPassword;

      const confirmPassword = rawInput.confirm_password;

      if (typeof challengeResponse !== "string" || !challengeResponse.trim()) {
        throw new Error("New password is required.");
      }

      if (confirmPassword && challengeResponse !== confirmPassword) {
        throw new Error("Passwords do not match.");
      }

      // Complete the challenge
      const result = await confirmSignIn({
        challengeResponse,
        options: input.options,
      });

      // Force session refresh so verifyUser step has valid auth
      try {
        await fetchAuthSession({ forceRefresh: true });
      } catch {
        // ignore session refresh failures
      }

      return result;
    },

    async handleForgotPassword(input: { username: string }) {

      const btn = document.querySelector<HTMLButtonElement>(
        ".login-form-wrapper form[data-amplify-authenticator-forgotpassword] button[type='submit']"
      );

      if (btn) btn.disabled = true; // disable button

      try {
        return await resetPassword({ username: input.username });
      } catch (error: unknown) {
        // Always show the same generic message for security (don't leak whether email is registered)
        throw new Error(
          "If this email is registered, you will receive an email."
        );
      } finally {
        if (btn) btn.disabled = false; // re-enable button
      }
    },
  };
  useEffect(() => {
    const check = async () => {
      try {
        const user = await getCurrentUser();
        if (user) router("/");
      } catch {
        // Not logged in – stay on login
      }
    };
    check();
  }, [router]);

  useEffect(() => {
    /* Structure: login-form-wrapper > div.login-wrapper (open/close) > form */
    const ensureLoginStructure = (form: Element) => {
      const parent = form.parentElement;
      if (!parent) return;
      parent.classList.add("login-form-wrapper");
      if (!parent.querySelector(":scope > div.login-wrapper")) {
        const loginWrapperDiv = document.createElement("div");
        loginWrapperDiv.className = "login-wrapper";
        parent.insertBefore(loginWrapperDiv, form);
      }
    };

    const rewriteAmplifyErrorMessage = (message: string) => {
      const msg = (message || "").trim();
      if (!msg) return msg;
      if (msg.includes("Confirmation code is required to confirmUserAttribute")) {
        return "Confirmation code is required";
      }

      // Genericize backend validation errors for OTP-like codes
      // Example: "1 validation error detected: Value at 'userCode' failed to satisfy constraint: Member must have length greater than or equal to 6"
      const lower = msg.toLowerCase();
      if (
        lower.includes("validation error detected") &&
        (lower.includes("value at 'usercode'") ||
          lower.includes('value at "usercode"') ||
          lower.includes("usercode")) &&
        (lower.includes("length greater than or equal to 6") ||
          lower.includes("length greater than or equal to") ||
          lower.includes("failed to satisfy constraint"))
      ) {
        return "Please enter a valid code.";
      }
      return msg;
    };

    const applyFormStyles = () => {
      const wrapper = document.querySelector(
        ".authenticator-wrapper [data-amplify-router-content] > div",
      );
      if (wrapper) wrapper.classList.add("login-form-wrapper");

      /* Ensure Amplify alert body text wraps safely */
      const ensureAlertBodyStylesInjected = () => {
        if (document.getElementById("bc-alert-body-styles")) return;
        const style = document.createElement("style");
        style.id = "bc-alert-body-styles";
        style.textContent = `
          .amplify-alert__body {
            word-break:break-word;
          }

          /* Prevent a flash of raw Cognito errors on Confirm Reset Password.
             We reveal alerts only after we have rewritten/sanitized the message. */
          .confirm-reset-page [role="alert"],
          .confirm-reset-page [class*="amplify-alert"],
          .confirm-reset-page [class*="amplify-text--error"] {
            visibility: hidden;
          }
          .confirm-reset-page [role="alert"][data-bc-rewritten="true"],
          .confirm-reset-page [class*="amplify-alert"][data-bc-rewritten="true"],
          .confirm-reset-page [class*="amplify-text--error"][data-bc-rewritten="true"] {
            visibility: visible;
          }

          /* Setup TOTP: hide optional link + secret copy section (temporary) */
          .setup-totp-page .hide-qr-unable-link,
          .setup-totp-page .totp-copy-helper,
          .setup-totp-page .hide-totp-secret {
            display: none !important;
          }
        `;
        document.head.appendChild(style);
      };

      ensureAlertBodyStylesInjected();

      // Normalize Amplify/Cognito error wording before the user sees it
      document
        .querySelectorAll(
          '[role="alert"], [class*="amplify-alert"], [class*="amplify-text--error"]',
        )
        .forEach((el) => {
          const node = el as HTMLElement;
          const current = node.textContent || "";
          const rewritten = rewriteAmplifyErrorMessage(current);
          if (rewritten) {
            if (rewritten !== current.trim()) node.textContent = rewritten;
            node.setAttribute("data-bc-rewritten", "true");
          }
        });

      const signinForm = document.querySelector(
        "form[data-amplify-authenticator-signin]",
      );
      const forgotForm = document.querySelector(
        "form[data-amplify-authenticator-forgotpassword]",
      );
      const confirmResetForm = document.querySelector(
        "form[data-amplify-authenticator-confirmresetpassword]",
      );
      const confirmSignUpForm =
        document.querySelector(
          "form[data-amplify-authenticator-confirmsignup]",
        ) ||
        document.querySelector(
          "form[data-amplify-authenticator-confirm-sign-up]",
        );
      const setupTotpForm =
        document.querySelector("form[data-amplify-authenticator-setuptotp]") ||
        document.querySelector("form[data-amplify-authenticator-setup-totp]");
      const confirmSignInForm =
        document.querySelector(
          "form[data-amplify-authenticator-confirmsignin]",
        ) ||
        document.querySelector(
          "form[data-amplify-authenticator-confirm-sign-in]",
        );
      const forceNewPasswordForm =
        document.querySelector(
          "form[data-amplify-authenticator-forcenewpassword]",
        ) ||
        document.querySelector(
          "form[data-amplify-authenticator-force-new-password]",
        );
      const verifyUserForm = document.querySelector(
        "form[data-amplify-authenticator-verifyuser]",
      );
      const confirmVerifyUserForm = document.querySelector(
        "form[data-amplify-authenticator-confirmverifyuser]",
      );
      if (signinForm) ensureLoginStructure(signinForm);
      if (forgotForm) {
        ensureLoginStructure(forgotForm);
        forgotForm.parentElement?.classList.add("forgot-page");
      }
      if (confirmResetForm) {
        ensureLoginStructure(confirmResetForm);
        confirmResetForm.parentElement?.classList.add("confirm-reset-page");
      }
      if (confirmSignUpForm) {
        ensureLoginStructure(confirmSignUpForm);
        confirmSignUpForm.parentElement?.classList.add("confirm-signup-page");
      }
      if (setupTotpForm) {
        ensureLoginStructure(setupTotpForm);
        setupTotpForm.parentElement?.classList.add("setup-totp-page");
      }
      if (confirmSignInForm) {
        ensureLoginStructure(confirmSignInForm);
        confirmSignInForm.parentElement?.classList.add("confirm-signin-page");
      }
      if (forceNewPasswordForm) {
        ensureLoginStructure(forceNewPasswordForm);
        forceNewPasswordForm.parentElement?.classList.add("change-password-page");
      }
      if (verifyUserForm) {
        ensureLoginStructure(verifyUserForm);
        verifyUserForm.parentElement?.classList.add("verify-user-page");
        // Hide Skip button (Amplify link-style button)
        verifyUserForm.querySelectorAll("a, button").forEach((el) => {
          const text = (el.textContent || "").trim();
          if (text === "Skip") {
            (el as HTMLElement).classList.add("hide-skip-account-recovery");
          }
        });
      }

      /* Verify User: custom radio styling to match theme */
      if (verifyUserForm) {
        const ensureRadioStylesInjected = () => {
          if (document.getElementById("bc-verify-radio-styles")) return;
          const style = document.createElement("style");
          style.id = "bc-verify-radio-styles";
          style.textContent = `
            /* Hide default Amplify radio UI on Verify User screen */
            .verify-user-page .amplify-radio__button,
            .verify-user-page .amplify-flex[data-amplify-radio] {
              display: none !important;
            }

            .verify-user-page .bc-radio-option {
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 12px 16px;
              margin-bottom: 12px;
              border-radius: 9999px;
              border: 1px solid rgba(148, 163, 184, 0.4);
              background: radial-gradient(circle at top left, rgba(231, 97, 160, 0.15), rgba(15, 23, 42, 0.95));
              color: #f9fafb;
              cursor: pointer;
              transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, transform 0.15s ease;
            }

            .verify-user-page .bc-radio-option:hover,
            .verify-user-page .bc-radio-option.bc-radio-hover {
              border-color: #e761a0;
              box-shadow: 0 0 0 1px rgba(231, 97, 160, 0.8), 0 18px 45px rgba(15, 23, 42, 0.9);
              background: radial-gradient(circle at top left, rgba(231, 97, 160, 0.25), rgba(15, 23, 42, 0.98));
              transform: translateY(-1px);
            }

            .verify-user-page .bc-radio-option input[type="radio"] {
              position: absolute;
              left: -9999px;
              opacity: 0;
              pointer-events: none;
              -webkit-appearance: none;
              -moz-appearance: none;
              appearance: none;
            }

            .verify-user-page .bc-radio-control {
              position: relative;
              flex: 0 0 auto;
              width: 0;
              height: 0;
              border-radius: 9999px;
              border: none;
              box-shadow: none;
              background: transparent;
              transition: opacity 0.12s ease, transform 0.12s ease;
            }
              .verify-user-page .amplify-flex amplify-radio__button{
              display: none !important;
              }

            .verify-user-page .bc-radio-control::after {
              content: "";
              position: absolute;
              left: -15PX;
              top: 50%;
              width: 18px;
              height: 18px;
              transform: translateY(-50%) scale(0.6);
              border-radius: 9999px;
              border: 2px solid rgba(148, 163, 184, 0.7);
              box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.9);
              background: radial-gradient(circle, #e761a0, #c026d3);
              opacity: 0;
              transition: opacity 0.12s ease, transform 0.12s ease, border-color 0.15s ease;
            }

            .verify-user-page .bc-radio-option input[type="radio"]:checked + .bc-radio-control::after {
              opacity: 1;
              transform: translateY(-50%) scale(1);
              border-color: #e761a0;
            }

            .verify-user-page .bc-radio-text {
              flex: 1 1 auto;
              font-size: 0.9rem;
              color: rgba(249, 250, 251, 0.9);
            }
          `;
          document.head.appendChild(style);
        };

        const styleVerifyRadios = () => {
          const radios = verifyUserForm.querySelectorAll<HTMLInputElement>(
            'input[type="radio"]',
          );
          if (!radios.length) return;

          ensureRadioStylesInjected();

          radios.forEach((radio) => {
            const label = radio.closest("label");
            if (!label) return;

            if (!label.classList.contains("bc-radio-option")) {
              label.classList.add("bc-radio-option");

              const existingControl = label.querySelector<HTMLSpanElement>(
                ".bc-radio-control",
              );
              let control = existingControl;
              if (!control) {
                control = document.createElement("span");
                control.className = "bc-radio-control";
              }

              const labelTextNodes: ChildNode[] = [];
              label.childNodes.forEach((node) => {
                if (node !== radio && node !== control && node.nodeType === Node.TEXT_NODE) {
                  if (node.textContent && node.textContent.trim()) {
                    labelTextNodes.push(node);
                  }
                }
              });

              const textWrapper =
                label.querySelector<HTMLSpanElement>(".bc-radio-text") ||
                document.createElement("span");
              textWrapper.className = "bc-radio-text";

              if (!textWrapper.textContent || !textWrapper.textContent.trim()) {
                const combined = labelTextNodes
                  .map((n) => n.textContent || "")
                  .join(" ")
                  .trim();
                if (combined) textWrapper.textContent = combined;
              }

              labelTextNodes.forEach((n) => label.removeChild(n));

              if (radio.nextSibling !== control) {
                label.insertBefore(control, radio.nextSibling);
              }

              if (control.nextSibling !== textWrapper) {
                label.insertBefore(textWrapper, control.nextSibling);
              }

              if (!(label as any)._bcHoverBound) {
                (label as any)._bcHoverBound = true;
                label.addEventListener("mouseenter", () => {
                  label.classList.add("bc-radio-hover");
                });
                label.addEventListener("mouseleave", () => {
                  label.classList.remove("bc-radio-hover");
                });
              }
            }
          });
        };

        styleVerifyRadios();
      }
      if (confirmVerifyUserForm) {
        ensureLoginStructure(confirmVerifyUserForm);
        const confirmVerifyWrapper = confirmVerifyUserForm.parentElement;
        confirmVerifyWrapper?.classList.add("confirm-verify-user-page");
        /* Hide Skip on Confirm Verify User (code entry) – check form and card wrapper */
        const confirmVerifyRoot = confirmVerifyWrapper ?? confirmVerifyUserForm;
        confirmVerifyRoot.querySelectorAll("a, button, [role='button']").forEach((el) => {
          const text = (el.textContent || "").trim();
          if (text === "Skip") {
            (el as HTMLElement).classList.add("hide-skip-account-recovery");
          }
        });
      }
      document.querySelectorAll(".forgot-page").forEach((el) => {
        if (
          !el.querySelector("form[data-amplify-authenticator-forgotpassword]")
        )
          el.classList.remove("forgot-page");
      });
      document.querySelectorAll(".confirm-reset-page").forEach((el) => {
        if (
          !el.querySelector(
            "form[data-amplify-authenticator-confirmresetpassword]",
          )
        )
          el.classList.remove("confirm-reset-page");
      });
      document.querySelectorAll(".confirm-signup-page").forEach((el) => {
        if (
          !el.querySelector("form[data-amplify-authenticator-confirmsignup]") &&
          !el.querySelector("form[data-amplify-authenticator-confirm-sign-up]")
        )
          el.classList.remove("confirm-signup-page");
      });
      document.querySelectorAll(".setup-totp-page").forEach((el) => {
        if (
          !el.querySelector("form[data-amplify-authenticator-setuptotp]") &&
          !el.querySelector("form[data-amplify-authenticator-setup-totp]")
        )
          el.classList.remove("setup-totp-page");
      });
      document.querySelectorAll(".confirm-signin-page").forEach((el) => {
        if (
          !el.querySelector("form[data-amplify-authenticator-confirmsignin]") &&
          !el.querySelector("form[data-amplify-authenticator-confirm-sign-in]")
        )
          el.classList.remove("confirm-signin-page");
      });

      const signinFormInWrapper = document.querySelector(
        ".login-form-wrapper form[data-amplify-authenticator-signin]",
      );
      const forgotFormInWrapper = document.querySelector(
        ".login-form-wrapper form[data-amplify-authenticator-forgotpassword]",
      );
      const confirmResetFormInWrapper = document.querySelector(
        ".login-form-wrapper form[data-amplify-authenticator-confirmresetpassword]",
      );
      const confirmSignUpFormInWrapper =
        document.querySelector(
          ".login-form-wrapper form[data-amplify-authenticator-confirmsignup]",
        ) ||
        document.querySelector(
          ".login-form-wrapper form[data-amplify-authenticator-confirm-sign-up]",
        );
      const setupTotpFormInWrapper =
        document.querySelector(
          ".login-form-wrapper form[data-amplify-authenticator-setuptotp]",
        ) ||
        document.querySelector(
          ".login-form-wrapper form[data-amplify-authenticator-setup-totp]",
        );
      const confirmSignInFormInWrapper =
        document.querySelector(
          ".login-form-wrapper form[data-amplify-authenticator-confirmsignin]",
        ) ||
        document.querySelector(
          ".login-form-wrapper form[data-amplify-authenticator-confirm-sign-in]",
        );
      const forceNewPasswordFormInWrapper =
        document.querySelector(
          ".login-form-wrapper form[data-amplify-authenticator-forcenewpassword]",
        ) ||
        document.querySelector(
          ".login-form-wrapper form[data-amplify-authenticator-force-new-password]",
        );
      const confirmVerifyUserFormInWrapper = document.querySelector(
        ".login-form-wrapper form[data-amplify-authenticator-confirmverifyuser]",
      );
      const form =
        signinFormInWrapper ||
        forgotFormInWrapper ||
        confirmResetFormInWrapper ||
        confirmSignUpFormInWrapper ||
        setupTotpFormInWrapper ||
        confirmSignInFormInWrapper ||
        forceNewPasswordFormInWrapper ||
        confirmVerifyUserFormInWrapper;
      if (!form) return;

      const isForgot = !!forgotFormInWrapper;
      const isConfirmReset = !!confirmResetFormInWrapper;
      const isConfirmSignUp = !!confirmSignUpFormInWrapper;
      const isSetupTotp = !!setupTotpFormInWrapper;
      const isConfirmSignIn = !!confirmSignInFormInWrapper;
      const isForceNewPassword = !!forceNewPasswordFormInWrapper;
      const isConfirmVerifyUser = !!confirmVerifyUserFormInWrapper;
      const inputSelector = isForgot
        ? 'input[name="username"]'
        : isConfirmReset
          ? 'input[name="password"], input[name="confirm_password"] ,input[name="confirmation_code"]'
          : isConfirmSignUp || isSetupTotp || isConfirmSignIn || isConfirmVerifyUser
            ? 'input[name="confirmation_code"]'
            : isForceNewPassword
              ? 'input[name="password"], input[name="confirm_password"]'
              : 'input[name="username"], input[name="password"]';

      form.querySelectorAll(inputSelector).forEach((input) => {
        input.classList.add("form-control");
        const parent = input.parentElement;
        if (!parent) return;
        if (!isConfirmSignUp && !isSetupTotp && !isConfirmSignIn && !isConfirmVerifyUser)
          parent.classList.add("form-input", "icon-wrap");
        const formGroup = parent.parentElement;
        if (formGroup) formGroup.classList.add("form-group");

        if (
          !parent.querySelector(".icon-img") &&
          !isConfirmSignUp &&
          !isSetupTotp &&
          !isConfirmSignIn &&
          !isConfirmVerifyUser
        ) {
          const iconSrc =
            input.getAttribute("name") === "username" ? emailIcon : lockIcon;
          const iconWrap = document.createElement("div");
          iconWrap.className = "icon-img";
          const icon = document.createElement("img");
          icon.src = iconSrc;
          icon.alt =
            input.getAttribute("name") === "username" ? "Email" : "Password";
          icon.setAttribute("aria-hidden", "true");
          iconWrap.appendChild(icon);
          parent.insertBefore(iconWrap, parent.firstChild);
        }
        const isPasswordField =
          input.getAttribute("name") === "password" ||
          input.getAttribute("name") === "confirm_password";
        // Check if this is a password field and we don’t already have a toggle
        if (
          isPasswordField &&
          !isConfirmSignUp &&
          !isSetupTotp &&
          !isConfirmSignIn &&
          !parent.querySelector(".password-toggle")
        ) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "password-toggle iconAyes";
          btn.setAttribute("aria-label", "Show password");
          btn.setAttribute("data-icon-state", "open");
          btn.appendChild(createEyeOpenSvg());

          btn.addEventListener("click", () => {
            const wrap = btn.parentElement;
            const inp = wrap?.querySelector<HTMLInputElement>("input");
            const formEl = wrap?.closest("form");
            if (!inp || !wrap || !formEl) return;

            const show = inp.type === "password";

            // Save cursor position to prevent jumping
            const start = inp.selectionStart;
            const end = inp.selectionEnd;

            inp.type = show ? "text" : "password";

            // Restore cursor position
            if (start !== null && end !== null) {
              inp.setSelectionRange(start, end);
            }

            // Update custom attributes
            const key =
              inp.getAttribute("name") === "confirm_password"
                ? "data-confirm-password-visible"
                : "data-password-visible";
            formEl.setAttribute(key, show ? "true" : "false");
            wrap.setAttribute("data-password-visible", show ? "true" : "false");
            btn.setAttribute("aria-label", show ? "Hide password" : "Show password");
            setPasswordToggleIcon(btn, show);
          });

          // Add button to the DOM
          parent.appendChild(btn);
          input.classList.add("has-password-toggle");
        }
        if (isConfirmSignUp || isSetupTotp || isConfirmSignIn || isConfirmVerifyUser) {
          input.classList.add("otp-code-input");
          parent?.classList.add("form-input", "confirm-signup-code-wrap");
        }
      });

      // Confirm Reset Password: validate code is exactly 6 digits (avoid leaking backend validation text)
      if (confirmResetFormInWrapper) {
        const confirmResetFormEl = confirmResetFormInWrapper as HTMLFormElement;
        const codeInput =
          confirmResetFormEl.querySelector<HTMLInputElement>(
            'input[name="confirmation_code"]',
          );

        if (codeInput) {
          codeInput.setAttribute("inputmode", "numeric");
          codeInput.setAttribute("autocomplete", "one-time-code");
          codeInput.setAttribute("pattern", "\\d{6}");
          codeInput.maxLength = 6;

          // Keep only digits while typing/pasting
          if (!(codeInput as any)._bcDigitsOnlyAttached) {
            (codeInput as any)._bcDigitsOnlyAttached = true;
            codeInput.addEventListener("input", () => {
              const digitsOnly = (codeInput.value || "").replace(/\D+/g, "");
              if (digitsOnly !== codeInput.value) codeInput.value = digitsOnly;
              if (codeInput.value.length > 6) codeInput.value = codeInput.value.slice(0, 6);
            });
          }
        }

        if (!(confirmResetFormEl as any)._bcCodeValidationAttached) {
          (confirmResetFormEl as any)._bcCodeValidationAttached = true;
          confirmResetFormEl.addEventListener(
            "submit",
            (e) => {
              const currentCode = (codeInput?.value || "").trim();
              const isValid = /^\d{6}$/.test(currentCode);
              if (isValid) return;

              e.preventDefault();
              e.stopPropagation();

              // Prefer an inline Amplify alert if present, otherwise toast
              const alertEl = confirmResetFormEl.querySelector<HTMLElement>(
                '[role="alert"], [class*="amplify-alert"], [class*="amplify-text--error"]',
              );
              if (alertEl) {
                alertEl.style.display = "";
                alertEl.textContent = "Please enter a valid code.";
                alertEl.setAttribute("data-bc-rewritten", "true");
              } else {
                toast.error("Please enter a valid code.");
              }
            },
            true,
          );
        }
      }

      /* Sync password toggle: read visibility from form (survives Amplify re-render); only update DOM when state is wrong to avoid double icon flip */
      form.querySelectorAll(".password-toggle").forEach((btn) => {
        const parent = (btn as HTMLElement).parentElement;
        const formEl = parent?.closest("form");
        if (!parent || !formEl) return;
        const inp = parent.querySelector("input[name='password'], input[name='confirm_password']") as HTMLInputElement | null;
        if (!inp) return;
        const visibilityKey = inp.getAttribute("name") === "confirm_password" ? "data-confirm-password-visible" : "data-password-visible";
        const visible = formEl.getAttribute(visibilityKey) === "true";
        const targetType = visible ? "text" : "password";
        if (inp.type !== targetType) {
          const value = inp.value;
          inp.type = targetType;
          inp.value = value;
        }
        parent.setAttribute("data-password-visible", visible ? "true" : "false");
        const btnEl = btn as HTMLElement;
        const iconStateMatches = btnEl.getAttribute("data-icon-state") === (visible ? "closed" : "open");
        if (!iconStateMatches) {
          btnEl.setAttribute("aria-label", visible ? "Hide password" : "Show password");
          setPasswordToggleIcon(btnEl, visible);
        }
      });

      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add(
          "w-100",
          "theme-btn",
          "text-uppercase",
          "mt-50",
        );
      }

      /* Forgot page: remove top "Back to Sign In" (keep only bottom "Back to Login") */
      if (forgotFormInWrapper) {
        forgotFormInWrapper
          .querySelectorAll("[data-amplify-footer]")
          .forEach((footer) => {
            if (!footer.querySelector(".login-forgot-wrap")) {
              footer.classList.add("hide-forgot-top-back");
            }
          });
        forgotFormInWrapper.querySelectorAll("a, button").forEach((el) => {
          const text = (el.textContent || "").trim();
          if (text === "Back to Sign In" && !el.closest(".login-forgot-wrap")) {
            const target = el.closest("[data-amplify-footer]") || el;
            target.classList.add("hide-forgot-top-back");
          }
        });
      }

      /* Confirm Reset Password: optionally style/hide \"Resend Code\" link */
      if (confirmResetFormInWrapper) {
        confirmResetFormInWrapper
          .querySelectorAll("a, button, span")
          .forEach((el) => {
            const text = (el.textContent || "").trim();
            if (text === "Resend Code") {
              const target = (el.closest("a, button") || el) as HTMLElement;
              target.classList.add("hide-resend-code");
            }
          });
      }

      /* Reset OTP form when Resend is clicked: clear input and messages */
      const attachResendReset = (formEl: Element) => {
        const resendNodes = formEl.querySelectorAll("a, button, span");
        for (let i = 0; i < resendNodes.length; i++) {
          const el = resendNodes[i];
          const text = (el.textContent || "").trim();
          if (text === "Resend Code" || text.includes("Resend")) {
            const linkOrButton = (el.closest("a, button") || el) as HTMLElement;
            if ((linkOrButton as HTMLElement & { _resendResetAttached?: boolean })._resendResetAttached) break;
            (linkOrButton as HTMLElement & { _resendResetAttached?: boolean })._resendResetAttached = true;
            linkOrButton.addEventListener("click", () => {
              const form = linkOrButton.closest("form");
              if (!form) return;
              form.querySelectorAll('input[name="confirmation_code"]').forEach((input) => {
                (input as HTMLInputElement).value = "";
              });
              form.querySelectorAll('[class*="amplify-alert"], [class*="amplify-text--error"], [role="alert"]').forEach((msg) => {
                (msg as HTMLElement).style.display = "none";
                (msg as HTMLElement).textContent = "";
              });
            });
            break;
          }
        }
      };

      /* Confirm Sign Up: style "Resend Code" link + attach reset on click */
      if (confirmSignUpFormInWrapper) {
        const nodes =
          confirmSignUpFormInWrapper.querySelectorAll("a, button, span");
        for (let i = 0; i < nodes.length; i++) {
          const el = nodes[i];
          const text = (el.textContent || "").trim();
          if (text === "Resend Code" || text.includes("Resend")) {
            const linkOrButton = (el.closest("a, button") || el) as HTMLElement;
            linkOrButton.classList.add("resend-code-link");
            const wrapper = linkOrButton.parentElement;
            if (wrapper && !wrapper.classList.contains("login-forgot-wrap")) {
              wrapper.classList.add(
                "login-forgot-wrap",
                "text-pink",
                "f-16",
                "font-weight-300",
                "text-decoration-underline",
              );
            }
            attachResendReset(confirmSignUpFormInWrapper);
            break;
          }
        }
      }

      /* Setup TOTP: hide "Back to Sign In", keep only bottom "Back to Login" */
      if (setupTotpFormInWrapper) {
        setupTotpFormInWrapper.querySelectorAll("a, button").forEach((el) => {
          const text = (el.textContent || "").trim();
          if (text === "Back to Sign In") {
            (el as HTMLElement).classList.add("hide-back-to-signin");
          }
        });

        // Hide "Unable to verify account through QR Code Link"
        setupTotpFormInWrapper
          .querySelectorAll("a, button, span")
          .forEach((el) => {
            const text = (el.textContent || "").trim().toLowerCase();
            if (!text) return;
            if (text.includes("unable to verify account through qr code link")) {
              (el.closest("a, button") || el).classList.add("hide-qr-unable-link");
            }
          });

        /* Temporary: hide the helper line + the secret key/copy section */
        const formEl = setupTotpFormInWrapper as HTMLFormElement;
        formEl.querySelectorAll(".totp-copy-helper").forEach((n) => n.remove());

        const copySection =
          formEl.querySelector("[data-amplify-copy]") ||
          formEl.querySelector("[class*='amplify-copy']") ||
          Array.from(formEl.querySelectorAll("[class*='amplify-field']")).find(
            (el) =>
              el.querySelector("button:not([type='submit'])") &&
              (el.textContent || "").trim().length > 10,
          );

        if (copySection) (copySection as HTMLElement).classList.add("hide-totp-secret");
      }

      /* Confirm Sign In (OTP): hide default "Back to Sign In"; style Resend + reset form on Resend click */
      if (confirmSignInFormInWrapper) {
        confirmSignInFormInWrapper
          .querySelectorAll("a, button")
          .forEach((el) => {
            const text = (el.textContent || "").trim();
            if (text === "Back to Sign In" || text === "Back to Sign In.") {
              (el as HTMLElement).classList.add("hide-back-to-signin");
            }
          });
        const resendNodes =
          confirmSignInFormInWrapper.querySelectorAll("a, button, span");
        for (let i = 0; i < resendNodes.length; i++) {
          const el = resendNodes[i];
          const text = (el.textContent || "").trim();
          if (text === "Resend Code" || text.includes("Resend")) {
            const linkOrButton = (el.closest("a, button") || el) as HTMLElement;
            linkOrButton.classList.add("resend-code-link");
            const wrapper = linkOrButton.parentElement;
            if (wrapper && !wrapper.classList.contains("login-forgot-wrap")) {
              wrapper.classList.add(
                "login-forgot-wrap",
                "text-pink",
                "f-16",
                "font-weight-300",
                "text-decoration-underline",
              );
            }
            attachResendReset(confirmSignInFormInWrapper);
            break;
          }
        }
      }
    };

    /* Run immediately so no flash of old theme; then again after paint and on interval for late-mounted forms */
    applyFormStyles();
    const t1 = setTimeout(applyFormStyles, 0);
    const t2 = setTimeout(applyFormStyles, 50);
    const id = setInterval(applyFormStyles, 500);

    // Real-time resend toast: wait for Amplify success/error message (not just click).
    let activeObserver: MutationObserver | null = null;
    let activeTimeout: number | null = null;

    const onResendClickCapture = (evt: MouseEvent) => {
      const target = evt.target as HTMLElement | null;
      if (!target) return;

      const clickable = (target.closest("button, a, span") || target) as HTMLElement;
      const text = (clickable.textContent || "").trim();
      if (!text) return;
      if (text !== "Resend Code" && !text.toLowerCase().includes("resend")) return;

      const form = clickable.closest("form") || document.querySelector("form[data-amplify-authenticator-confirmsignup], form[data-amplify-authenticator-confirmsignin], form[data-amplify-authenticator-confirmresetpassword]");
      if (!form) return;

      const toastId = "auth-resend-code";
      toast.loading("Sending code…", { id: toastId });

      const alertSelector =
        '[role="alert"], [class*="amplify-alert"], [class*="amplify-text--error"]';

      const decide = (message: string) => {
        const msg = rewriteAmplifyErrorMessage(message || "");
        if (!msg) return null;
        const lower = msg.toLowerCase();

        const isError =
          lower.includes("error") ||
          lower.includes("failed") ||
          lower.includes("too many") ||
          lower.includes("limit") ||
          lower.includes("try again") ||
          lower.includes("invalid") ||
          lower.includes("cannot") ||
          lower.includes("expired");

        const isSuccess =
          lower.includes("sent") ||
          lower.includes("code") ||
          lower.includes("delivery") ||
          lower.includes("check your") ||
          lower.includes("we've emailed") ||
          lower.includes("we have emailed");

        if (isError) return { type: "error" as const, message: msg };
        if (isSuccess) return { type: "success" as const, message: "Code has been sent." };
        return null;
      };

      const finish = (result: { type: "success" | "error"; message: string }) => {
        if (activeObserver) {
          activeObserver.disconnect();
          activeObserver = null;
        }
        if (activeTimeout) {
          window.clearTimeout(activeTimeout);
          activeTimeout = null;
        }
        if (result.type === "error") toast.error(result.message, { id: toastId });
        else toast.success(result.message, { id: toastId });
      };

      // Clear previous watcher for rapid consecutive clicks
      if (activeObserver) activeObserver.disconnect();
      if (activeTimeout) window.clearTimeout(activeTimeout);

      const existing = (form as Element).querySelector<HTMLElement>(alertSelector);
      const existingResult = existing ? decide(existing.textContent || "") : null;
      if (existingResult) {
        finish(existingResult);
        return;
      }

      activeObserver = new MutationObserver(() => {
        const alertEl = (form as Element).querySelector<HTMLElement>(alertSelector);
        const result = alertEl ? decide(alertEl.textContent || "") : null;
        if (result) finish(result);
      });
      activeObserver.observe(form as Element, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Fallback: if Amplify doesn't render any message, don't leave spinner forever
      activeTimeout = window.setTimeout(() => {
        finish({ type: "success", message: "Code has been sent." });
      }, 7000);
    };

    document.addEventListener("click", onResendClickCapture, true);
    const root = document.querySelector(
      ".authenticator-wrapper [data-amplify-router-content]",
    );
    const observer =
      root && typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => applyFormStyles())
        : null;
    if (observer && root)
      observer.observe(root, { childList: true, subtree: true });

    // Prevent "flash" of raw Amplify/Cognito alert text by rewriting immediately on insert.
    // This runs at the document level so it works even before our wrapper classes are applied.
    const alertSelector =
      '[role="alert"], [class*="amplify-alert"], [class*="amplify-text--error"]';
    const alertObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver((mutations) => {
          for (const m of mutations) {
            for (const added of Array.from(m.addedNodes)) {
              if (!(added instanceof HTMLElement)) continue;

              const candidates: HTMLElement[] = [];
              if (added.matches?.(alertSelector)) candidates.push(added);
              added
                .querySelectorAll?.(alertSelector)
                .forEach((n) => candidates.push(n as HTMLElement));

              for (const node of candidates) {
                // Skip if we've already processed it
                if (node.getAttribute("data-bc-rewritten") === "true") continue;

                const current = (node.textContent || "").trim();
                if (!current) continue;

                const rewritten = rewriteAmplifyErrorMessage(current);
                if (!rewritten || rewritten === current) continue;

                // Hide first to avoid a paint with the old message.
                node.style.visibility = "hidden";
                node.textContent = rewritten;
                node.setAttribute("data-bc-rewritten", "true");
                node.style.visibility = "visible";
              }
            }
          }
        })
        : null;

    alertObserver?.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearInterval(id);
      observer?.disconnect();
      alertObserver?.disconnect();
      document.removeEventListener("click", onResendClickCapture, true);
      if (activeObserver) activeObserver.disconnect();
      if (activeTimeout) window.clearTimeout(activeTimeout);
    };
  }, []);

  return (
    <div className="login-main d-flex flex-column align-items-center justify-content-center min-vh-100 relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 2px 2px, #E761A0 1.5px, transparent 0)",
          backgroundSize: "28px 28px",
          backgroundPosition: "0 0",
          maskImage:
            "radial-gradient(ellipse 100% 60% at 50% 100%, black, transparent 60%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 100% 60% at 50% 100%, black, transparent 60%)",
          opacity: 0.5,
        }}
      />
      <div className="login-wrapper relative z-10 d-flex flex-column align-items-center justify-content-center w-100">
        <LoginRateLimitProvider>
          <Authenticator
            className="authenticator-wrapper"
            formFields={formFields}
            components={authenticatorComponents}
            services={services}
            hideSignUp={true}
          >
            {({ user }) => (
              <div className="mx-auto card-wrap login-card custom-card">
                <LoginRedirect user={user} />
              </div>
            )}
          </Authenticator>
        </LoginRateLimitProvider>
      </div>
    </div>
  );
};

export default Login;

// -----------------------------------------------------------------------------
// Sign-in rate limit: track failures, show cooldown, disable submit when locked
// -----------------------------------------------------------------------------
function SignInRateLimitBanner() {
  const { route, error } = useAuthenticator((ctx) => [ctx.route, ctx.error]);
  const rateLimit = useContext(LoginRateLimitContext);
  const lastCountedErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (route !== "signIn" || !rateLimit) return;
    if (error && error !== lastCountedErrorRef.current) {
      lastCountedErrorRef.current = error;
      rateLimit.recordFailure();
    }
    if (!error) lastCountedErrorRef.current = null;
  }, [route, error, rateLimit]);

  useEffect(() => {
    if (!rateLimit?.isLocked) return;
    const form = document.querySelector("form[data-amplify-authenticator-signin]");
    const submitBtn = form?.querySelector<HTMLButtonElement>("button[type='submit']");
    if (submitBtn) submitBtn.disabled = true;
    return () => {
      if (submitBtn) submitBtn.disabled = false;
    };
  }, [rateLimit?.isLocked]);

  useEffect(() => {
    if (rateLimit?.isLocked) return;
    const form = document.querySelector("form[data-amplify-authenticator-signin]");
    const submitBtn = form?.querySelector<HTMLButtonElement>("button[type='submit']");
    if (submitBtn) submitBtn.disabled = false;
  }, [rateLimit?.isLocked]);

  if (!rateLimit?.isLocked) return null;
  return (
    <div
      className="mb-4 p-3 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-200 text-center text-sm"
      role="alert"
    >
      Too many failed attempts. Try again in <strong>{rateLimit.cooldownRemaining}</strong> seconds.
    </div>
  );
}

// -----------------------------------------------------------------------------
// Authenticator UI overrides
// -----------------------------------------------------------------------------
const authenticatorComponents = {
  Header() {
    return <View />;
  },

  Footer() {
    const { toSignIn, route } = useAuthenticator();
    useEffect(() => {
      if (route === "signUp") {
        toSignIn();
      }
    }, [route, toSignIn]);
    return (
      <View>
        <div className="logon-footer mt-30 flex justify-between items-end">
          <div className="f-16 font-weight-300 text-white text-left">
            <p className="mb-0">BuildCompliance 2026</p>
            <p className="mb-0">AJ Digital</p>
          </div>
          {/* <Link
            to="/help"
            className="text-dark-gery f-20 font-weight-600 text-uppercase flex justify-between items-center"
          >
            <img src={HELP_SRC} alt="Help" className="help-icon mr-30" />
            HELP
          </Link> */}
        </div>
      </View>
    );
  },
  ForceNewPassword: {
    Header() {
      return <ChangePasswordHeader />;
    },
    FormFields() {
      const { validationErrors } = useAuthenticator();

      const splitValidationMessage = (value: unknown): string[] => {
        if (!value) return [];
        const text = String(value).trim();
        if (!text) return [];

        // Cognito often concatenates requirements like:
        // "Password must ...Password must ...Password must ..."
        if (text.includes("Password must")) {
          const parts = text
            .split(/(?=Password must)/g)
            .map((s) => s.trim())
            .filter(Boolean);
          if (parts.length > 1) return parts;
        }

        // If provider includes explicit new lines, keep each line separate.
        const newlineParts = text
          .split(/\r?\n+/g)
          .map((s) => s.trim())
          .filter(Boolean);
        if (newlineParts.length > 1) return newlineParts;

        return [text];
      };

      const passwordLines = splitValidationMessage(validationErrors?.password);
      const confirmPasswordLines = splitValidationMessage(
        validationErrors?.confirm_password
      );

      return (
        <>
          <Authenticator.Provider>
            <div className="form-group">
              <div className="form-input icon-wrap">
                <input
                  name="password"
                  type="password"
                  placeholder="Create new password"
                  className="form-control"
                  required
                />
              </div>
              {passwordLines.length > 0 &&
                passwordLines.map((line, idx) => (
                  <p
                    key={idx}
                    style={{
                      color: "#DC3545",
                      fontSize: "16px",
                      marginTop: idx === 0 ? "8px" : "4px",
                      marginBottom: 0,
                    }}
                  >
                    {line}
                  </p>
                ))}
            </div>
            <div className="form-group">
              <div className="form-input icon-wrap">
                <input
                  name="confirm_password"
                  type="password"
                  placeholder="Confirm new password"
                  className="form-control"
                  required
                />
              </div>
              {confirmPasswordLines.length > 0 &&
                confirmPasswordLines.map((line, idx) => (
                  <p
                    key={idx}
                    style={{
                      color: "#DC3545",
                      fontSize: "16px",
                      marginTop: idx === 0 ? "8px" : "4px",
                      marginBottom: 0,
                    }}
                  >
                    {line}
                  </p>
                ))}
            </div>
          </Authenticator.Provider>
        </>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      return (
        <div className="login-forgot-wrap change-password-back-link">
          <button type="button" onClick={toSignIn}>
            Back to Sign In
          </button>
        </div>
      );
    },
  },

  SignIn: {
    Header() {
      return (
        <>
          <SignInRateLimitBanner />
          <div className="login-wrapper">
            <div className="login-img mx-auto d-flex justify-content-center align-items-center">
              <Image
                alt="Login"
                src={LOGO_SRC}
                className="login-img-logo mx-auto"
              />
            </div>
            <div className="head-wrap">
              <h2 className="custom-heading f-36 font-weight-300 text-center text-white ">
                Login to your account
              </h2>
              <p
                className="text-center text-white f-16 font-weight-300 mb-30"
                style={{ color: "rgba(255,255,255,0.8)" }}
              >
                Enter your email and password to sign in.
              </p>
            </div>
          </div>
        </>
      );
    },
    Footer() {
      const { toForgotPassword } = useAuthenticator();
      return (
        <div className="login-forgot-wrap text-pink f-16 font-weight-300 text-decoration-underline">
          <Button
            fontWeight="normal"
            onClick={toForgotPassword}
            size="small"
            variation="link"
          >
            Forgot password?
          </Button>
        </div>
      );
    },
  },

  SignUp: {
    Header() {
      return <DefaultRouteHeader title="Create a new account" />;
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      return (
        <View textAlign="center">
          <div className="back-btn">
            <Button
              fontWeight="normal"
              onClick={toSignIn}
              size="small"
              variation="link"
            >
              Back to Sign In
            </Button>
          </div>
        </View>
      );
    },
  },

  ConfirmSignUp: {
    Header() {
      return (
        <div className="login-wrapper">
          <div className="login-img mx-auto d-flex justify-content-center align-items-center">
            <Image
              alt="BuildCompliance"
              src={LOGO_SRC}
              className="login-img-logo mx-auto"
            />
          </div>
          <div className="head-wrap">
            <h2 className="custom-heading f-36 font-weight-300 text-center text-white  mb-0">
              We’ve emailed you a code
            </h2>
            <p className="text-center text-white f-16 font-weight-300">
              Enter the 6 digit code sent to your email.
            </p>
          </div>
        </div>
      );
    },
    Footer() {
      const navigate = useNavigate();
      // authStatus transitions from 'unauthenticated' to 'authenticated'
      const { route, authStatus } = useAuthenticator((context) => [
        context.route,
        context.authStatus,
      ]);

      useEffect(() => {
        // 1. Success case: The user is now fully signed in
        if (authStatus === "authenticated") {
          navigate("/dashboard");
        }

        // 2. Manual fallback: Only if you strictly want them back at login
        // and you have disabled Auto Sign-In in your configuration.
      }, [authStatus, route, navigate]);

      return null;
    },
  },
  SetupTotp: {
    Header() {
      return (
        <div className="login-wrapper ">
          <div className="login-img mx-auto d-flex justify-content-center align-items-center">
            <Image
              alt="BuildCompliance"
              src={LOGO_SRC}
              className="login-img-logo mx-auto"
            />
          </div>
          <div className="head-wrap">
            <p
              className="text-center text-white f-16 font-weight-300 mb-20"
              style={{ color: "rgba(255,255,255,0.9)" }}
            >
              Please scan this QR code in the Google Auth app
            </p></div>
        </div>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      return (
        <div className="login-forgot-wrap text-pink f-16 font-weight-300">
          <Button
            fontWeight="normal"
            onClick={toSignIn}
            size="small"
            variation="link"
          >
            Back to Login
          </Button>
        </div>
      );
    },
  },
  ConfirmSignIn: {
    Header() {
      return (
        <div className="login-wrapper ">
          <div className="login-img mx-auto d-flex justify-content-center align-items-center">
            <Image
              alt="BuildCompliance"
              src={LOGO_SRC}
              className="login-img-logo mx-auto"
            />
          </div>
          <div className="head-wrap">
            <h2 className="custom-heading f-36 font-weight-300 text-center text-white ">
              Verification Code
            </h2>
            <p
              className="text-center text-white f-16 font-weight-300 mb-30"
              style={{ color: "rgba(255,255,255,0.8)" }}
            >
              Enter your verification code from the authenticator app
            </p>
          </div>
        </div>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      return (
        <div className="login-forgot-wrap text-pink f-16 font-weight-300">
          <Button
            fontWeight="normal"
            onClick={toSignIn}
            size="small"
            variation="link"
          >
            Back to Login
          </Button>
        </div>
      );
    },
  },
  ForgotPassword: {
    Header() {
      return (
        <div className="login-wrapper ">
          <div className="login-img mx-auto d-flex justify-content-center align-items-center">
            <Image
              alt="Login"
              src={LOGO_SRC}
              className="login-img-logo mx-auto"
            />
          </div>
          <div className="head-wrap">
            <h2 className="custom-heading f-36 font-weight-300 text-center text-white ">
              Forgot your password?
            </h2>
            <p
              className="text-center text-white f-16 font-weight-300 mb-30"
              style={{ color: "rgba(255,255,255,0.8)" }}
            >
              Enter your email and we&apos;ll send a link to reset your password.
            </p>
          </div>
        </div>
      );
    },
    Footer() {
      const { toSignIn, error } = useAuthenticator();
      useEffect(() => {
        if (error === "Cannot reset password for the user as there is no registered/verified email or phone_number") {
          setTimeout(() => {
            toSignIn();
          }, 3000);
        }
      }, [error, toSignIn]);
      return (
        <div className="login-forgot-wrap text-pink f-16 font-weight-300 ">
          <Button
            fontWeight="normal"
            onClick={toSignIn}
            size="small"
            variation="link"
          >
            Back to Login
          </Button>
        </div>
      );
    },
  },
  ConfirmResetPassword: {
    Header() {
      return (
        <ChangePasswordHeader />
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      return (
        <div className="login-forgot-wrap change-password-back-link">
          <button type="button" onClick={toSignIn}>
            Back to Sign In
          </button>
        </div>
      );
    },
  },
  VerifyUser: {
    Header() {
      return (
        <div className="login-wrapper">
          <div className="login-img mx-auto d-flex justify-content-center align-items-center">
            <Image
              alt="BuildCompliance"
              src={LOGO_SRC}
              className="login-img-logo mx-auto"
            />
          </div>
          <div className="head-wrap">
            <h2 className="custom-heading f-36 font-weight-300 text-center text-white ">
              Verify Account
            </h2>
            <p
              className="text-center text-white f-16 font-weight-300 mb-30"
              style={{ color: "rgba(255,255,255,0.8)" }}
            >
              Please click below to verify your email address.
            </p>
          </div>
        </div>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      return (
        <div className="login-forgot-wrap change-password-back-link">
          <button type="button" onClick={toSignIn}>
            Back to Sign In
          </button>
        </div>
      );
    },
  },
  ConfirmVerifyUser: {
    Header() {
      return (
        <div className="login-wrapper">
          <div className="login-img mx-auto d-flex justify-content-center align-items-center">
            <Image
              alt="BuildCompliance"
              src={LOGO_SRC}
              className="login-img-logo mx-auto"
            />
          </div>
          <div className="head-wrap">
            <h2 className="custom-heading f-36 font-weight-300 text-center text-white ">
              Account Verification
            </h2>
            <p
              className="text-center text-white f-16 font-weight-300 mb-30"
              style={{ color: "rgba(255,255,255,0.8)" }}
            >
              Please enter the code sent to your email
            </p>
          </div>
        </div>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      return (
        <div className="login-forgot-wrap change-password-back-link">
          <button type="button" onClick={toSignIn}>
            Back to Sign In
          </button>
        </div>
      );
    },
  },
  SelectMfaType: {
    Header: () => <Heading level={3}>Select Desired MFA Type</Heading>,
    Footer: DefaultRouteFooter,
  },
  SetupEmail: {
    Header: () => <Heading level={3}>Email MFA Setup</Heading>,
    Footer: DefaultRouteFooter,
  },
};

